// WOS-BUNDLE: the Work OS work extras (parity report items 6, 11, 15, 16) — company tags, task
// links, favorites, recents, duplicate, My Tasks sections, task time tracking with a timer, and a
// usage CSV export. Base plan (`feature: core`). Work OS sources (app/src): `domain/task-extras.ts`
// (tags, links), `domain/queries.ts` `favoritesFor`, `domain/recents.ts`, `domain/tasks.ts`
// `duplicateTask` / `myTasks` / `setMyTaskBucket`, `domain/containers.ts` `duplicateContainerTree`,
// `domain/time.ts`; routes `api/tags`, `api/tasks/[id]/{tags,links,duplicate,time}`,
// `api/favorites`, `api/me/{recents,sections,bucket}`, `api/containers/[id]/duplicate`.
// Webhook auto-pause and container scope are on the webhook routes (`org.ts`).
import { z } from "zod";
import {
  FavoriteKind,
  MyTaskBuiltinSection,
  RecentKind,
  TagColor,
  TaskLinkRelation,
} from "../enums";
import { IsoDate, Timestamp, text, Uuid } from "../primitives";
import { TimeEntry } from "../rows";
import { Favorite, MyTaskPlacement, MyTaskSection, Tag } from "../work-bundle-rows";
import { Board, Space } from "../work-rows";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";
import { TaskDetail } from "./work";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;
const TagName = text(60);

// Tags -----------------------------------------------------------------------------------------

/** A tag with how many tasks carry it (Settings → Tags). */
export const TagView = Tag.extend({ task_count: z.int().min(0) }).meta({ id: "TagView" });
export const TagList = z.object({ data: z.array(TagView) }).meta({ id: "TagList" });
export const TagCreate = z
  .strictObject({
    name: TagName,
    color: TagColor.optional().describe("Default: picked from the name (Work OS `ensureTag`)"),
  })
  .meta({ id: "TagCreate" });
export const TagPatch = z
  .strictObject({ name: TagName.optional(), color: TagColor.optional() })
  .meta({ id: "TagPatch" });
export const TagMerge = z
  .strictObject({ into_tag_id: Uuid.describe("The tag that stays; this one is removed") })
  .meta({ id: "TagMerge" });
/** Tag a task by id, or by name (made when no tag has it, ignoring case: Work OS `tagTask`). */
export const TaskTagAdd = z
  .strictObject({
    tag_id: Uuid.optional(),
    name: TagName.optional(),
    color: TagColor.optional().describe("For a tag made by name"),
  })
  .refine((b) => (b.tag_id === undefined) !== (b.name === undefined), {
    message: "give exactly one of tag_id and name",
  })
  .meta({ id: "TaskTagAdd" });

// Links ----------------------------------------------------------------------------------------

/** The other end of a link as the caller may see it (a hidden task is not listed). */
export const LinkedTask = z
  .object({
    id: Uuid,
    number: z.int().min(1),
    title: z.string(),
    status: z.string(),
    board_id: Uuid,
    completed_at: Timestamp.nullable(),
  })
  .meta({ id: "LinkedTask" });
/**
 * One link read from this task: `relates_to`, `duplicates` / `duplicated_by` (task links), and
 * `blocks` / `blocked_by` (hard dependencies, shown with the links).
 */
export const TaskLinkView = z
  .object({ relation: TaskLinkRelation, task: LinkedTask, created_at: Timestamp })
  .meta({ id: "TaskLinkView" });
export const TaskLinkList = z.object({ data: z.array(TaskLinkView) }).meta({ id: "TaskLinkList" });
export const TaskLinkAdd = z
  .strictObject({ relation: TaskLinkRelation, task_id: Uuid })
  .meta({ id: "TaskLinkAdd" });
export const TaskLinkRemoveQuery = z.object({ relation: TaskLinkRelation });

// Duplicate ------------------------------------------------------------------------------------

/** Work OS copies tags, fields, checklists and the description; the rest is asked here. */
export const TaskDuplicate = z
  .strictObject({
    title: text(500).optional().describe('Default: "<title> (copy)"'),
    subtasks: z
      .boolean()
      .optional()
      .describe("Copy subtasks (with the same options); default false"),
    checklists: z.boolean().optional().describe("Copy checklists, items reopened; default true"),
    files: z
      .boolean()
      .optional()
      .describe("Reference the task's files from the copy (no bytes copied); default false"),
    assignees: z.boolean().optional().describe("Keep the assignees; default false"),
  })
  .meta({ id: "TaskDuplicate" });
export const BoardDuplicate = z
  .strictObject({
    name: text(120).optional().describe('Default: "Copy of <name>"'),
    tasks: z.boolean().optional().describe("Copy the board's tasks too; default false"),
  })
  .meta({ id: "BoardDuplicate" });
export const SpaceDuplicate = BoardDuplicate.meta({ id: "SpaceDuplicate" });

// Favorites and recents ------------------------------------------------------------------------

/** A star as the sidebar shows it (only what the caller may still open). */
export const FavoriteView = z
  .object({
    kind: FavoriteKind,
    id: Uuid,
    title: z.string(),
    position: z.int(),
    /** Where it lives: the space (boards, docs, tasks, channels) and the board (tasks). */
    space_id: Uuid.nullable(),
    board_id: Uuid.nullable(),
  })
  .meta({ id: "FavoriteView" });
export const FavoriteList = z.object({ data: z.array(FavoriteView) }).meta({ id: "FavoriteList" });
export const FavoriteAdd = z
  .strictObject({ kind: FavoriteKind, object_id: Uuid })
  .meta({ id: "FavoriteAdd" });
export const FavoriteParams = z.object({ co: Uuid, kind: FavoriteKind, id: Uuid });
export const FavoriteOrder = z
  .strictObject({
    items: z
      .array(z.strictObject({ kind: FavoriteKind, object_id: Uuid }))
      .max(200)
      .describe("The caller's stars in their new order; stars not named keep their place after"),
  })
  .meta({ id: "FavoriteOrder" });

export const RecentView = z
  .object({
    kind: RecentKind,
    id: Uuid,
    title: z.string(),
    /** Where it lives, outermost first (space, board), as names. */
    path: z.array(z.string()),
    space_id: Uuid.nullable(),
    board_id: Uuid.nullable(),
    viewed_at: Timestamp,
  })
  .meta({ id: "RecentView" });
export const RecentList = z.object({ data: z.array(RecentView) }).meta({ id: "RecentList" });
export const RecentRecord = z
  .strictObject({ kind: RecentKind, object_id: Uuid })
  .meta({ id: "RecentRecord" });

// My Tasks sections ----------------------------------------------------------------------------

/** A section of My Tasks: one of the four built-ins (`id` is its key) or the caller's own. */
export const MySectionView = z
  .object({
    id: z.string().describe("A built-in key (recent, today, upcoming, later) or a section id"),
    name: z.string(),
    builtin: z.boolean(),
    position: z.int(),
  })
  .meta({ id: "MySectionView" });
export const MySections = z
  .object({
    sections: z.array(MySectionView),
    /** Where the caller put its tasks; a task without a row is in `recent`. */
    placements: z.array(MyTaskPlacement.pick({ task_id: true, section: true, position: true })),
  })
  .meta({ id: "MySections" });
export const MySectionCreate = z.strictObject({ name: text(80) }).meta({ id: "MySectionCreate" });
export const MySectionPatch = z
  .strictObject({
    name: text(80).optional(),
    position: z.int().min(0).optional().describe("The section's place among the caller's own"),
  })
  .meta({ id: "MySectionPatch" });
export const MyTaskPlace = z
  .strictObject({
    section: z.union([MyTaskBuiltinSection, Uuid]),
    position: z.int().min(0).optional().describe("Its place in the section; default last"),
  })
  .meta({ id: "MyTaskPlace" });

// Time -----------------------------------------------------------------------------------------

/** A running timer with its task, as the top bar shows it. */
export const TimerView = z
  .object({
    task_id: Uuid,
    task_number: z.int().min(1),
    task_title: z.string(),
    board_id: Uuid,
    seat_id: Uuid,
    started_at: Timestamp,
    note: z.string().nullable(),
  })
  .meta({ id: "TimerView" });
export const MyTimer = z.object({ timer: TimerView.nullable() }).meta({ id: "MyTimer" });
export const TimerStop = z
  .strictObject({ note: z.string().max(2000).optional() })
  .meta({ id: "TimerStop" });
export const TimerStopped = z
  .object({ timer: z.null(), time_entry: TimeEntry.nullable() })
  .meta({ id: "TimerStopped" });

/** One booked entry on a task, with who booked it. */
export const TaskTimeEntry = z
  .object({
    id: Uuid,
    membership_id: Uuid,
    seat_id: Uuid,
    work_date: IsoDate,
    started_at: Timestamp.nullable(),
    ended_at: Timestamp.nullable(),
    duration_seconds: z.int().positive(),
    source: z.string(),
    approval_status: z.string(),
    note: z.string().nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "TaskTimeEntry" });
export const TaskTime = z
  .object({
    entries: z.array(TaskTimeEntry),
    /** Booked human time (live entries, rejected ones excluded). */
    total_seconds: z.int().min(0),
    /** Read-only: digital employees' run time on the task (runs it woke). */
    agent_seconds: z.int().min(0),
    /** The caller's own timer when it runs on this task. */
    running: TimerView.nullable(),
  })
  .meta({ id: "TaskTime" });
export const TaskTimeLog = z
  .strictObject({
    minutes: z
      .int()
      .min(1)
      .max(24 * 60),
    note: z.string().max(2000).optional(),
    work_date: IsoDate.optional().describe("Default: today in the company's time zone"),
  })
  .meta({ id: "TaskTimeLog" });
export const BoardTime = z
  .object({
    total_seconds: z.int().min(0),
    agent_seconds: z.int().min(0),
    tasks: z.array(z.object({ task_id: Uuid, seconds: z.int().min(0) })),
  })
  .meta({ id: "BoardTime" });

// Usage export ---------------------------------------------------------------------------------

export const UsageExportQuery = z.object({
  from: IsoDate.optional().describe("Default: the first day of this month"),
  to: IsoDate.optional().describe("Inclusive; default today"),
});

// Routes ---------------------------------------------------------------------------------------

const taskSub = (path: string) => `${base}/tasks/{id}/${path}`;
const TaskSubParams = z.object({ co: Uuid, id: Uuid, item_id: Uuid });

export const workBundleRoutes = {
  "tags.list": defineRoute({
    ...core,
    id: "tags.list",
    method: "get",
    path: `${base}/tags`,
    tag: "tags",
    summary: "The company's tags by name, with how many tasks carry each (WOS-BUNDLE)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: TagList },
    errors: [],
    emits: [],
  }),
  "tags.create": defineRoute({
    ...core,
    id: "tags.create",
    method: "post",
    path: `${base}/tags`,
    tag: "tags",
    summary: "Make a company tag (a name taken, ignoring case, is a conflict)",
    access: "member",
    params: CompanyParams,
    body: TagCreate,
    response: { status: 201, schema: Tag },
    errors: ["conflict"],
    emits: ["tag.created"],
  }),
  "tags.update": defineRoute({
    ...core,
    id: "tags.update",
    method: "patch",
    path: `${base}/tags/{id}`,
    tag: "tags",
    summary: "Rename or recolour a tag (Settings → Tags)",
    access: "manager",
    params: CompanyItemParams,
    body: TagPatch,
    response: { status: 200, schema: Tag },
    errors: ["conflict"],
    emits: ["tag.updated"],
  }),
  "tags.merge": defineRoute({
    ...core,
    id: "tags.merge",
    method: "post",
    path: `${base}/tags/{id}/merge`,
    tag: "tags",
    summary: "Merge this tag into another: its tasks get the other tag, this one is deleted",
    access: "manager",
    params: CompanyItemParams,
    body: TagMerge,
    response: { status: 200, schema: Tag },
    errors: [],
    emits: ["tag.merged", "task.tagged"],
  }),
  "tags.delete": defineRoute({
    ...core,
    id: "tags.delete",
    method: "delete",
    path: `${base}/tags/{id}`,
    tag: "tags",
    summary: "Delete a tag (taken off every task)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["tag.deleted"],
  }),
  "tasks.addTag": defineRoute({
    ...core,
    id: "tasks.addTag",
    method: "post",
    path: taskSub("tags"),
    tag: "tasks",
    summary: "Tag the task by tag id or by name (a new name makes the tag; WOS-BUNDLE)",
    access: "member",
    params: CompanyItemParams,
    body: TaskTagAdd,
    response: { status: 201, schema: Tag },
    // TEMPLATES-T3/T4: an agent's tag write held to its policy (field_set).
    errors: ["policy_denied", "conflict"],
    emits: ["task.tagged", "tag.created"],
  }),
  "tasks.removeTag": defineRoute({
    ...core,
    id: "tasks.removeTag",
    method: "delete",
    path: taskSub("tags/{item_id}"),
    tag: "tasks",
    summary: "Take a tag off the task (`item_id` is the tag)",
    access: "member",
    params: TaskSubParams,
    response: { status: 204 },
    errors: ["policy_denied", "conflict"],
    emits: ["task.untagged"],
  }),
  "tasks.links": defineRoute({
    ...core,
    id: "tasks.links",
    method: "get",
    path: taskSub("links"),
    tag: "tasks",
    summary:
      "The task's links (relates to, duplicates, duplicated by) and hard dependencies (blocks, blocked by), to tasks the caller may see",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TaskLinkList },
    errors: [],
    emits: [],
  }),
  "tasks.addLink": defineRoute({
    ...core,
    id: "tasks.addLink",
    method: "post",
    path: taskSub("links"),
    tag: "tasks",
    summary:
      "Link the task to another (`blocks` / `blocked_by` add a hard dependency; editor of both tasks)",
    access: "member",
    params: CompanyItemParams,
    body: TaskLinkAdd,
    response: { status: 201, schema: TaskLinkView },
    errors: ["conflict", "dependency_cycle"],
    emits: ["task.linked", "task.dependency_changed"],
  }),
  "tasks.removeLink": defineRoute({
    ...core,
    id: "tasks.removeLink",
    method: "delete",
    path: taskSub("links/{item_id}"),
    tag: "tasks",
    summary:
      "Remove a link (`item_id` is the other task, `?relation=` the link read from this task)",
    access: "member",
    params: TaskSubParams,
    query: TaskLinkRemoveQuery,
    response: { status: 204 },
    errors: [],
    emits: ["task.unlinked", "task.dependency_changed"],
  }),
  "tasks.duplicate": defineRoute({
    ...core,
    id: "tasks.duplicate",
    method: "post",
    path: taskSub("duplicate"),
    tag: "tasks",
    summary:
      'Copy the task next to it as "<title> (copy)" — tags, fields, description, checklists; subtasks, files and assignees when asked',
    access: "member",
    params: CompanyItemParams,
    body: TaskDuplicate,
    response: { status: 201, schema: TaskDetail },
    errors: [],
    emits: [
      "task.created",
      "task.duplicated",
      "page.created",
      "task.tagged",
      "task.field_changed",
      "task.checklist_changed",
      "task.assigned",
    ],
  }),
  "boards.duplicate": defineRoute({
    ...core,
    id: "boards.duplicate",
    method: "post",
    path: `${base}/boards/{id}/duplicate`,
    tag: "boards",
    summary:
      'Copy a board in its space as "Copy of <name>": statuses, fields, views and settings; its tasks when asked',
    access: "member",
    params: CompanyItemParams,
    body: BoardDuplicate,
    response: { status: 201, schema: Board },
    errors: [],
    emits: [
      "board.created",
      "board.duplicated",
      "field_def.created",
      "view.created",
      "channel.created",
      "task.created",
      "page.created",
      "task.tagged",
      "task.checklist_changed",
    ],
  }),
  "spaces.duplicate": defineRoute({
    ...core,
    id: "spaces.duplicate",
    method: "post",
    path: `${base}/spaces/{id}/duplicate`,
    tag: "boards",
    summary:
      'Copy a space as "Copy of <name>" with its boards (structure and settings); their tasks when asked. Access grants are not copied',
    access: "member",
    params: CompanyItemParams,
    body: SpaceDuplicate,
    response: { status: 201, schema: Space },
    errors: [],
    emits: [
      "space.created",
      "space.duplicated",
      "board.created",
      "board.duplicated",
      "field_def.created",
      "view.created",
      "channel.created",
      "channel.member_changed",
      "access.granted",
      "task.created",
      "page.created",
      "task.tagged",
      "task.checklist_changed",
    ],
  }),
  "favorites.list": defineRoute({
    ...core,
    id: "favorites.list",
    method: "get",
    path: `${base}/my/favorites`,
    tag: "me",
    summary: "The caller's stars in its order (only what it may still open; WOS-BUNDLE)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: FavoriteList },
    errors: [],
    emits: [],
  }),
  "favorites.add": defineRoute({
    ...core,
    id: "favorites.add",
    method: "post",
    path: `${base}/my/favorites`,
    tag: "me",
    summary: "Star a space, board, doc, task or channel the caller may open (last in the order)",
    access: "member",
    params: CompanyParams,
    body: FavoriteAdd,
    response: { status: 201, schema: Favorite },
    errors: [],
    emits: ["favorite.added"],
  }),
  "favorites.remove": defineRoute({
    ...core,
    id: "favorites.remove",
    method: "delete",
    path: `${base}/my/favorites/{kind}/{id}`,
    tag: "me",
    summary: "Unstar something",
    access: "member",
    params: FavoriteParams,
    response: { status: 204 },
    errors: [],
    emits: ["favorite.removed"],
  }),
  "favorites.reorder": defineRoute({
    ...core,
    id: "favorites.reorder",
    method: "put",
    path: `${base}/my/favorites/order`,
    tag: "me",
    summary: "Reorder the caller's Favorites (drag in the sidebar)",
    access: "member",
    params: CompanyParams,
    body: FavoriteOrder,
    response: { status: 200, schema: FavoriteList },
    errors: [],
    emits: ["favorite.reordered"],
  }),
  "recents.list": defineRoute({
    ...core,
    id: "recents.list",
    method: "get",
    path: `${base}/my/recents`,
    tag: "me",
    summary:
      "The caller's last ten opened tasks, docs, boards and spaces, newest first (only what it may still open)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: RecentList },
    errors: [],
    emits: [],
  }),
  "recents.record": defineRoute({
    ...core,
    id: "recents.record",
    method: "post",
    path: `${base}/my/recents`,
    tag: "me",
    summary: "Note that the caller opened something (move to front, ten kept)",
    access: "member",
    params: CompanyParams,
    body: RecentRecord,
    response: { status: 204 },
    errors: [],
    emits: ["recent.viewed"],
  }),
  "mySections.list": defineRoute({
    ...core,
    id: "mySections.list",
    method: "get",
    path: `${base}/my/sections`,
    tag: "me",
    summary: "My Tasks sections (the four built-ins, then the caller's own) and its placements",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: MySections },
    errors: [],
    emits: [],
  }),
  "mySections.create": defineRoute({
    ...core,
    id: "mySections.create",
    method: "post",
    path: `${base}/my/sections`,
    tag: "me",
    summary: "Add a My Tasks section (after the others)",
    access: "member",
    params: CompanyParams,
    body: MySectionCreate,
    response: { status: 201, schema: MyTaskSection },
    errors: [],
    emits: ["my_task_section.created"],
  }),
  "mySections.update": defineRoute({
    ...core,
    id: "mySections.update",
    method: "patch",
    path: `${base}/my/sections/{id}`,
    tag: "me",
    summary: "Rename or move one of the caller's My Tasks sections",
    access: "member",
    params: CompanyItemParams,
    body: MySectionPatch,
    response: { status: 200, schema: MyTaskSection },
    errors: [],
    emits: ["my_task_section.updated"],
  }),
  "mySections.delete": defineRoute({
    ...core,
    id: "mySections.delete",
    method: "delete",
    path: `${base}/my/sections/{id}`,
    tag: "me",
    summary: "Delete one of the caller's sections; its tasks go back to Recently assigned",
    access: "member",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["my_task_section.deleted"],
  }),
  "my.placeTask": defineRoute({
    ...core,
    id: "my.placeTask",
    method: "put",
    path: `${base}/my/tasks/{id}/section`,
    tag: "me",
    summary: "Put a task of My Tasks in a section, at a place (drag between sections)",
    access: "member",
    params: CompanyItemParams,
    body: MyTaskPlace,
    response: { status: 200, schema: MyTaskPlacement },
    errors: [],
    emits: ["my_task.placed"],
  }),
  "tasks.time": defineRoute({
    ...core,
    id: "tasks.time",
    method: "get",
    path: taskSub("time"),
    tag: "tasks",
    summary: "Time booked on the task, its total, agents' run time, and the caller's running timer",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TaskTime },
    errors: [],
    emits: [],
  }),
  "tasks.logTime": defineRoute({
    ...core,
    id: "tasks.logTime",
    method: "post",
    path: taskSub("time"),
    tag: "tasks",
    summary: "Book time on the task by hand, on one of the caller's seats",
    access: "member",
    params: CompanyItemParams,
    body: TaskTimeLog,
    response: { status: 201, schema: TimeEntry },
    errors: ["no_seat"],
    emits: ["time_entry.created"],
  }),
  "tasks.deleteTime": defineRoute({
    ...core,
    id: "tasks.deleteTime",
    method: "delete",
    path: taskSub("time/{item_id}"),
    tag: "tasks",
    summary: "Remove a time entry of the task (the caller's own; a manager: anyone's)",
    access: "member",
    params: TaskSubParams,
    response: { status: 204 },
    errors: [],
    emits: ["time_entry.deleted", "cost.posted"],
  }),
  "boards.time": defineRoute({
    ...core,
    id: "boards.time",
    method: "get",
    path: `${base}/boards/{id}/time`,
    tag: "boards",
    summary: "Time booked on the board's tasks (visible ones), per task, and agents' run time",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: BoardTime },
    errors: [],
    emits: [],
  }),
  "timer.get": defineRoute({
    ...core,
    id: "timer.get",
    method: "get",
    path: `${base}/my/timer`,
    tag: "me",
    summary: "The caller's running task timer, if any",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: MyTimer },
    errors: [],
    emits: [],
  }),
  "timer.start": defineRoute({
    ...core,
    id: "timer.start",
    method: "post",
    path: taskSub("timer"),
    tag: "tasks",
    summary:
      "Start the caller's timer on the task (a timer running elsewhere is stopped and booked first)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 201, schema: TimerView },
    errors: ["no_seat"],
    emits: ["task.timer_started", "task.timer_stopped", "time_entry.created"],
  }),
  "timer.stop": defineRoute({
    ...core,
    id: "timer.stop",
    method: "post",
    path: `${base}/my/timer/stop`,
    tag: "me",
    summary: "Stop the caller's timer and book the time on its task (at least a minute)",
    access: "member",
    params: CompanyParams,
    body: TimerStop,
    response: { status: 200, schema: TimerStopped },
    errors: [],
    emits: ["task.timer_stopped", "time_entry.created"],
  }),
  "usage.export": defineRoute({
    ...core,
    id: "usage.export",
    method: "get",
    path: `${base}/usage/export`,
    tag: "reports",
    summary:
      "Usage as CSV (Reports): one row per cost entry in the window — date, kind, seat, agent, run, model, tokens, amount",
    access: "manager",
    params: CompanyParams,
    query: UsageExportQuery,
    response: { status: 200, contentType: "application/octet-stream" },
    errors: [],
    emits: [],
  }),
} as const;
